/**
 * The page editor's block list, apart from React (Prototype › Pages › Edit;
 * backlog ea559f76, 08ef8abc, ba51325b): removing a block with an Undo in its
 * place, and when a new block's empty fields start to complain.
 */

type Block = { type: string; [key: string]: any };

/** A block taken out in this edit: `at` is the index it goes back to. */
export interface RemovedBlock {
  key: string;
  at: number;
  block: Block;
}

export function removeBlock(
  blocks: Block[],
  removed: RemovedBlock[],
  i: number,
  key: string
): { blocks: Block[]; removed: RemovedBlock[] } {
  if (!blocks[i]) return { blocks, removed };
  return {
    blocks: blocks.filter((_, j) => j !== i),
    removed: [
      ...removed.map((r) => (r.at > i ? { ...r, at: r.at - 1 } : r)),
      { key, at: i, block: blocks[i] },
    ],
  };
}

/** The block back where it was (or last, if the list has since got shorter). */
export function undoRemove(
  blocks: Block[],
  removed: RemovedBlock[],
  key: string
): { blocks: Block[]; removed: RemovedBlock[]; at: number } | null {
  const hit = removed.find((r) => r.key === key);
  if (!hit) return null;
  const at = Math.min(hit.at, blocks.length);
  return {
    blocks: [...blocks.slice(0, at), hit.block, ...blocks.slice(at)],
    removed: removed
      .filter((r) => r.key !== key)
      .map((r) => (r.at >= at ? { ...r, at: r.at + 1 } : r)),
    at,
  };
}

export type BlockRow = { kind: 'block'; i: number } | { kind: 'removed'; removed: RemovedBlock };

/** The list as shown: each removed row in its block's place. */
export function blockRows(count: number, removed: RemovedBlock[]): BlockRow[] {
  const rows: BlockRow[] = [];
  const here = (test: (r: RemovedBlock) => boolean) =>
    removed.filter(test).forEach((r) => rows.push({ kind: 'removed', removed: r }));
  for (let i = 0; i < count; i++) {
    here((r) => r.at === i);
    rows.push({ kind: 'block', i });
  }
  here((r) => r.at >= count);
  return rows;
}

export const removedNote = (agentOnly: boolean) =>
  agentOnly
    ? 'Your agent made this block, so you can’t add it back from here. Undo puts it back. After you save, only your agent can rebuild it.'
    : 'It comes off the page when you save. Until then you can undo.';

/** The problem a field's data-ce-key answers for, where the two differ. */
const problemKey = (ceKey: string) => (ceKey === 'button.label' ? 'label' : ceKey);

/** A new block's fields the person has left: their problems may show. */
export function leftField(block: Block, ceKey: string): Block {
  const key = problemKey(ceKey);
  return block._left?.includes(key) ? block : { ...block, _left: [...(block._left || []), key] };
}

/**
 * What a block shows of its problems. One someone just added is empty on
 * purpose: nothing shows until a field has been left, or Save or Publish was
 * pressed (_reveal). Every other block shows them all, as before.
 */
export function shownProblems(block: Block, all: Record<string, string>): Record<string, string> {
  if (!block._new || block._reveal) return all;
  const left: string[] = block._left || [];
  return Object.fromEntries(Object.entries(all).filter(([k]) => left.includes(k)));
}

/** The input to send someone to for a block's first problem. */
export function problemField(all: Record<string, string>): string | null {
  const first = Object.keys(all)[0];
  if (!first) return null;
  return first === 'label' ? 'button.label' : first;
}
